import { useEffect, useRef, useState } from 'react'

/**
 * Hook to manage injecting the FilteringButton into the DataGrid toolbar.
 * This exists to inject the FilteringButton after the first button in the toolbar,
 * to resemble DataGrid's toolbar structure but with our own filtering button implementation.
 *
 * Uses MutationObserver to handle cases where the toolbar renders after the initial mount.
 *
 * @param enabled - When false, skips portal injection entirely (useful for compact mode where no toolbar exists).
 */
export default function useFilteringButtonInjection({ enabled = true }: { enabled?: boolean } = {}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (!enabled || !containerRef.current) return

    let portalContainer: HTMLDivElement | null = null

    const tryInjectPortal = (): boolean => {
      if (!containerRef.current || portalContainer) return false

      const toolbar = containerRef.current.querySelector('.MuiDataGrid-mainContent div[role="toolbar"]')
      if (!toolbar) return false

      const firstButton = toolbar.querySelector('button')
      if (!firstButton) return false

      // Create a container element for the portal
      portalContainer = document.createElement('div')
      portalContainer.style.display = 'inline-flex'

      // Insert after the first button to resemble DataGrid's toolbar structure.
      firstButton.insertAdjacentElement('afterend', portalContainer)
      setPortalTarget(portalContainer)
      return true
    }

    // Try immediately in case toolbar already exists
    if (tryInjectPortal()) return () => portalContainer?.remove()

    // Otherwise, watch for DOM changes until toolbar appears
    const observer = new MutationObserver(() => {
      if (tryInjectPortal()) {
        observer.disconnect()
      }
    })

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true
    })

    return () => {
      observer.disconnect()
      portalContainer?.remove()
    }
  }, [enabled])

  return { containerRef, portalTarget }
}
